import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { CodeEditor } from "../components/CodeEditor.tsx";
import { SchedaContainer } from "../components/Container.tsx";
import { DeployConsole, NOMI_OPERAZIONE, useEsecuzione } from "../components/DeployConsole.tsx";
import { TestoAnsi } from "../components/TestoLog.tsx";
import { EnvEditor } from "../components/EnvEditor.tsx";
import { Intestazione } from "../components/Intestazione.tsx";
import { PannelloDiviso } from "../components/PannelloDiviso.tsx";
import { SchedaRilascio, type DatiInstradamenti } from "../components/Rilascio.tsx";
import { SchedaRisorse } from "../components/Risorse.tsx";
import { PannelloValidazione } from "../components/Variables.tsx";
import { Apribile, Badge, Button, Card, Empty, ErrorNotice, Field, Input, Spinner, cx } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { segmentaAnsi } from "../lib/log-ansi.ts";
import { formattaDurata } from "../lib/esecuzione.ts";
import { useT } from "../lib/i18n.tsx";
import { useAction, useQuery } from "../lib/hooks.ts";
import { useAltezzaDisponibile } from "../lib/altezza.ts";
import {
  dichiaraStackEnv,
  variabiliConDefault,
  variabiliDiPorta,
  variabiliUsate,
} from "../lib/highlight.ts";
import { comprimi, differenzaRighe, differenzaVariabili } from "../lib/differenze.ts";
import { variabiliModificate } from "../lib/risoluzione.ts";
import { linkProps, navigate } from "../lib/router.ts";
import {
  SCHEDE,
  leggiStato,
  scriviStato,
  type Scheda,
  type SezioneRilascio,
  type StatoPagina,
} from "../lib/risorse.ts";
import type { Azione } from "../lib/azioni-verifica.ts";
import type {
  DeployStrategy,
  Deployment,
  DeploymentLog,
  RevisionSummary,
  Session,
  Stack,
  StackValidation,
  StackConRiepilogo,
  StackDetail,
} from "../lib/types.ts";
import { formatoData } from "./Stacks.tsx";
import { dataSqlite, haPanoramica, schedaPredefinita } from "../lib/stato-stack.ts";

/**
 * Dettaglio di uno stack: panoramica, configurazione, rilascio e storico.
 *
 * L'ordine delle azioni riflette il modo di lavorare che Portman incoraggia:
 * si salva, si verifica, e solo dopo si avvia. Salvare non richiede che la
 * configurazione sia completa.
 */


/** Ciò che serve alle viste di una revisione: la risposta del server, riepilogo escluso. */
type DettaglioRevisione = Omit<StackDetail, "riepilogo">;

export function StackDetailPage({ stackId, sessione }: { stackId: number; sessione: Session }) {
  const t = useT();
  // I valori sensibili restano nascosti finché non vengono richiesti: aprire uno
  // stack durante una condivisione dello schermo non deve esporre le password.
  const [segretiVisibili, setSegretiVisibili] = useState(false);
  const dettaglio = useQuery<StackDetail>(
    `/stacks/${stackId}${segretiVisibili ? "?reveal=true" : ""}`,
  );
  // Scheda, finestra e servizio vivono nell'URL: il link di un avviso apre la
  // pagina già posizionata, e una ricarica riapre la stessa vista.
  const [stato, setStato] = useState<StatoPagina>(() => leggiStato(location.search));
  const setScheda = (nome: Scheda, sezione?: SezioneRilascio) =>
    setStato((s) => ({ ...s, scheda: nome, ...(sezione && { sezione }) }));
  // La scheda predefinita si decide una volta sola, al primo caricamento: dopo
  // il primo avvio cambierebbe sotto l'utente, smontando la bozza che sta scrivendo.
  const predefinita = useRef<Scheda | null>(null);
  useEffect(() => {
    // Sostituire, non aggiungere: cambiare scheda non deve riempire la cronologia.
    history.replaceState(history.state, "", location.pathname + scriviStato(stato));
  }, [stato]);

  /**
   * Richiesta arrivata dalla scheda di verifica.
   *
   * Porta con sé il contesto — quale variabile impostare, quale termine cercare
   * nel compose — così passare da un problema alla sua correzione non richiede
   * di ripetere a mano un'informazione che si ha già.
   */
  const [richiesta, setRichiesta] = useState<Azione | null>(null);

  const puòAvviare = sessione.permissions.includes("stack:deploy");
  const puòScrivere = sessione.permissions.includes("stack:write");

  // Il verdetto blue/green: dice nella striscia cosa farà davvero il prossimo
  // avvio. Basta `stack:read`, quindi lo vede chiunque apra la pagina. La
  // scheda Rilascio legge questo stesso: due letture potrebbero contraddirsi.
  const instradamenti = useQuery<DatiInstradamenti>(`/stacks/${stackId}/instradamenti`);

  // Il secondo argomento è la via per tornare a seguire l'operazione se il
  // collegamento cade: il deploy prosegue nel server, e senza questo l'unica
  // cosa che l'interfaccia potrebbe mostrare è un errore mai avvenuto.
  const esecuzione = useEsecuzione(
    useCallback(() => dettaglio.reload(), [dettaglio.reload]),
    `/stacks/${stackId}/operazione`,
  );

  // Un'operazione avviata prosegue anche se la pagina viene chiusa: all'apertura
  // si verifica se ce n'è una in corso e la si riprende a seguire.
  const { riaggancia } = esecuzione;
  useEffect(() => {
    void riaggancia(`/stacks/${stackId}/operazione`);
  }, [stackId, riaggancia]);

  // Un'operazione può partire anche mentre la pagina è aperta, da un webhook o
  // da un altro utente: senza accorgersene, i pulsanti resterebbero cliccabili.
  const seguendo = esecuzione.esito === "in-corso";
  useEffect(() => {
    if (seguendo) return;
    const timer = setInterval(async () => {
      const stato = await api.get<{ running: boolean }>(`/stacks/${stackId}/operazione/stato`).catch(() => null);
      if (stato?.running) void riaggancia(`/stacks/${stackId}/operazione`);
    }, CONTROLLO_OPERAZIONE_MS);
    return () => clearInterval(timer);
  }, [stackId, seguendo, riaggancia]);

  // Lo spinner solo al primo caricamento: le ricariche successive (dopo un
  // salvataggio, un rilascio, un instradamento) lasciano montate le schede, e
  // con esse la bozza non salvata.
  if (!dettaglio.data) return dettaglio.error ? <ErrorNotice error={dettaglio.error} /> : <Spinner className="tenue" />;

  // Il server manda il riepilogo accanto allo stack, non dentro: le viste lo vogliono insieme.
  const stack: StackConRiepilogo = { ...dettaglio.data.stack, riepilogo: dettaglio.data.riepilogo };
  // I segreti contano come visibili solo quando i valori veri sono arrivati:
  // fino ad allora la bozza resta quella con i valori mascherati, in sola lettura.
  const rivelati = dettaglio.percorso?.endsWith("?reveal=true") ?? false;
  // Senza `?scheda=` decide lo stato: chi non ha mai avviato lo stack non ha niente da guardare.
  predefinita.current ??= schedaPredefinita(stack);
  const scheda = stato.scheda ?? predefinita.current;

  return (
    <div className="space-y-5">
      <ErrorNotice error={dettaglio.error} />
      <Intestazione
        stack={stack}
        verdetto={instradamenti.data?.verdetto ?? null}
        sessione={sessione}
        esecuzione={esecuzione}
        onCambiato={dettaglio.reload}
        onScheda={setScheda}
      />

      {/* Scorrono su finestre strette invece di allargare la pagina: una
          pagina che scorre in orizzontale sposta anche tutto il resto. */}
      <nav className="senza-barra flex gap-1 overflow-x-auto border-b">
        {SCHEDE.map((nome) => (
          <button
            key={nome}
            onClick={() => setScheda(nome)}
            className={cx(
              "-mb-px shrink-0 border-b-2 px-3.5 py-2 text-sm capitalize",
              scheda === nome
                ? "border-accent-600 font-medium"
                : "border-transparent tenue hover:text-[var(--testo)]",
            )}
          >
            {t.t(`schede.${nome}` as const)}
          </button>
        ))}
      </nav>

      {scheda === "panoramica" &&
        (!haPanoramica(stack) ? (
          <Empty
            title={t.t("panoramica.vuoto")}
            hint={
              <>
                {t.t("panoramica.vuotoAiuto")}
                <span className="mt-4 flex justify-center gap-2">
                  <Button onClick={() => setScheda("configurazione")}>{t.t("panoramica.vaiConfigurazione")}</Button>
                  {puòAvviare && (
                    <Button
                      variant="primary"
                      loading={esecuzione.esito === "in-corso"}
                      onClick={() => void esecuzione.avvia(`/stacks/${stackId}/deploy`)}
                    >
                      {t.t("dettaglio.avvia")}
                    </Button>
                  )}
                </span>
              </>
            }
          />
        ) : (
          <SchedaContainer stackId={stackId} />
        ))}
      {scheda === "risorse" && (
        <SchedaRisorse
          stackId={stackId}
          stato={stato}
          onCambia={(parziale) => setStato((s) => ({ ...s, ...parziale }))}
          puòScrivere={puòScrivere}
        />
      )}
      {(scheda === "configurazione" || scheda === "rilascio") && (
        // La bozza è condivisa fra le due schede, quindi vive nel contenitore:
        // passare al rilascio non deve buttare via le modifiche in corso. La
        // `key` la reinizializza quando i segreti rivelati arrivano, perché con
        // essi arrivano i valori reali dal server.
        <SchedeModifica
          key={String(rivelati)}
          scheda={scheda}
          dettaglio={dettaglio.data}
          onSalvato={dettaglio.reload}
          modificabile={puòScrivere}
          segretiVisibili={rivelati}
          onRivela={() => setSegretiVisibili(true)}
          richiesta={richiesta}
          onAzione={(azione) => {
            setRichiesta(azione);
            setScheda("configurazione");
          }}
          rilascio={(controlli) => (
            <SchedaRilascio
              stackId={stackId}
              dati={instradamenti.data}
              errore={instradamenti.error}
              modificabile={sessione.permissions.includes("webhook:manage")}
              controlli={controlli}
              sezione={stato.sezione ?? "controlli"}
              onSezione={(sezione) => setScheda("rilascio", sezione)}
              // Strategia, domini e verdetto stanno anche nella striscia in alto.
              onInstradamentiAggiornati={() => {
                instradamenti.reload();
                dettaglio.reload();
              }}
              onConfigurazione={() => setScheda("configurazione")}
            />
          )}
        />
      )}
      {scheda === "storico" && (
        <SchedaStorico
          stackId={stackId}
          esecuzione={esecuzione}
          puòAvviare={puòAvviare}
          puòScrivere={puòScrivere}
          onRipristinato={dettaglio.reload}
        />
      )}

      {/* Sta in fondo al markup ma non in fondo alla pagina: la console è
          ancorata al fondo della finestra, così resta visibile qualunque scheda
          sia aperta — compreso lo storico, da cui si avviano i ripristini.
          L'annullamento è offerto solo a chi potrebbe anche avviare: fermare un
          rilascio è un'azione sullo stack come le altre. */}
      <DeployConsole
        esecuzione={esecuzione}
        onAnnulla={
          puòAvviare ? () => api.post(`/stacks/${stackId}/annulla`).then(() => {}) : undefined
        }
      />
    </div>
  );
}

/** Ogni quanto la pagina chiede se sullo stack è partita un'operazione altrove. */
const CONTROLLO_OPERAZIONE_MS = 5_000;

/**
 * La bozza in corso di modifica: compose, environment e messaggio di revisione.
 *
 * Vive sopra le schede perché non appartiene a nessuna di esse: la
 * configurazione la modifica in grande, la verifica la corregge puntualmente, e
 * il salvataggio è lo stesso. Tenerla in una scheda significherebbe perderla
 * cambiando scheda e doverne duplicare il salvataggio.
 */
function useBozza(dettaglio: DettaglioRevisione, onSalvato: () => void) {
  const [compose, setCompose] = useState(dettaglio.composeYaml);
  const [env, setEnv] = useState<Record<string, string>>(dettaglio.env);
  const [messaggio, setMessaggio] = useState("");

  const variabiliCambiate = useMemo(
    () => variabiliModificate(dettaglio.env, env),
    [dettaglio.env, env],
  );

  const modificato = compose !== dettaglio.composeYaml || variabiliCambiate.length > 0;

  const salvataggio = useAction(async () => {
    await api.post(`/stacks/${dettaglio.stack.id}/revisions`, {
      composeYaml: compose,
      env,
      message: messaggio || null,
    });
    setMessaggio("");
    onSalvato();
  });

  /** Torna alla revisione salvata, scartando la bozza. */
  const annulla = useCallback(() => {
    setCompose(dettaglio.composeYaml);
    setEnv(dettaglio.env);
    setMessaggio("");
  }, [dettaglio.composeYaml, dettaglio.env]);

  /** Imposta una variabile senza toccare le altre. */
  const impostaVariabile = useCallback(
    (nome: string, valore: string) => setEnv((precedente) => ({ ...precedente, [nome]: valore })),
    [],
  );

  return {
    compose,
    setCompose,
    env,
    setEnv,
    impostaVariabile,
    messaggio,
    setMessaggio,
    modificato,
    variabiliCambiate,
    salvataggio,
    annulla,
  };
}

type Bozza = ReturnType<typeof useBozza>;

/**
 * Le due schede che modificano lo stack, con la bozza in comune.
 *
 * Configurazione e rilascio guardano la stessa cosa da due distanze diverse:
 * l'una per scrivere, l'altra per controllare e correggere il singolo punto.
 */
function SchedeModifica({
  scheda,
  dettaglio,
  onSalvato,
  modificabile,
  segretiVisibili,
  onRivela,
  richiesta,
  onAzione,
  rilascio,
}: {
  scheda: "configurazione" | "rilascio";
  dettaglio: DettaglioRevisione;
  onSalvato: () => void;
  modificabile: boolean;
  segretiVisibili: boolean;
  onRivela: () => void;
  richiesta: Azione | null;
  onAzione: (azione: Azione) => void;
  /** La scheda Rilascio, che colloca i controlli sulla bozza fra le sue sezioni. */
  rilascio: (controlli: ReactNode) => ReactNode;
}) {
  const bozza = useBozza(dettaglio, onSalvato);

  if (scheda === "rilascio") {
    return rilascio(
      <SchedaVerifica
        stackId={dettaglio.stack.id}
        strategia={dettaglio.stack.deployStrategy}
        bozza={bozza}
        modificabile={modificabile}
        segretiVisibili={segretiVisibili}
        onRivela={onRivela}
        onAzione={onAzione}
      />,
    );
  }

  return (
    <SchedaConfigurazione
      dettaglio={dettaglio}
      bozza={bozza}
      modificabile={modificabile}
      segretiVisibili={segretiVisibili}
      onRivela={onRivela}
      richiesta={richiesta}
    />
  );
}

function SchedaConfigurazione({
  dettaglio,
  bozza,
  modificabile,
  segretiVisibili,
  onRivela,
  richiesta,
}: {
  dettaglio: DettaglioRevisione;
  bozza: Bozza;
  modificabile: boolean;
  segretiVisibili: boolean;
  onRivela: () => void;
  richiesta?: Azione | null;
}) {
  const { compose, setCompose, env, setEnv, messaggio, setMessaggio, modificato, salvataggio, annulla } =
    bozza;
  const t = useT();

  // Ricalcolate a ogni battuta: l'editor dell'environment segnala subito quali
  // variabili il compose richiede, senza attendere un giro di validazione.
  const richieste = useMemo(() => variabiliUsate(compose), [compose]);
  const gestiteDaPortman = useMemo(() => variabiliDiPorta(compose), [compose]);
  const conValorePredefinito = useMemo(() => variabiliConDefault(compose), [compose]);
  const consegnaTutto = useMemo(() => dichiaraStackEnv(compose), [compose]);

  // Gli editor crescono fino al fondo della finestra: il salvataggio sta nel
  // banner in alto, che galleggia sopra la pagina e non chiede spazio.
  const areaEditor = useRef<HTMLDivElement>(null);
  const altezzaEditor = useAltezzaDisponibile(areaEditor, { riservato: 16, minima: 320 });

  return (
    <div className="space-y-3">
      {/* Compare solo con modifiche in sospeso: senza, non c'è niente da
          salvare né da annullare. Sotto l'editor a schermo intero (z-50), che
          ha il suo pulsante di salvataggio. */}
      {modificabile && modificato && (
        <div
          role="region"
          aria-label={t.t("dettaglio.nonSalvate")}
          className="fixed inset-x-0 top-3 z-40 mx-auto w-[min(48rem,calc(100%-2rem))] space-y-2 rounded-xl border bg-[var(--superficie-alta)] p-3 shadow-lg transition duration-200 starting:-translate-y-4 starting:opacity-0 motion-reduce:transition-none"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{t.t("dettaglio.nonSalvate")}</span>
            <Input
              value={messaggio}
              onChange={(e) => setMessaggio(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !salvataggio.running && void salvataggio.run()}
              placeholder={t.t("dettaglio.messaggioRevisione")}
              className="min-w-48 flex-1"
            />
            <Button disabled={salvataggio.running} onClick={annulla}>
              {t.t("dettaglio.annullaModifiche")}
            </Button>
            <Button variant="primary" loading={salvataggio.running} onClick={() => void salvataggio.run()}>
              {t.t("dettaglio.salvaRevisione")}
            </Button>
          </div>
          <ErrorNotice error={salvataggio.error} />
        </div>
      )}

      {/* L'altezza si misura da qui: gli editor arrivano fino al fondo della finestra. */}
      <div ref={areaEditor}>
        <PannelloDiviso
          memoria="stack-configurazione"
          sinistra={
            <div className="group space-y-1.5 pr-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">docker-compose.yml</span>
                <span className="text-xs tenue">{t.t("dettaglio.aiutoEditor")}</span>
              </div>

              <CodeEditor
                value={compose}
                onChange={setCompose}
                linguaggio="yaml"
                readOnly={!modificabile}
                altezza={altezzaEditor}
                espandibile
                ricercabile
                // Chi arriva dalla verifica sa già cosa cercare: la ricerca si
                // apre sul termine, invece di chiedere di riscriverlo.
                {...(richiesta?.tipo === "cerca-nel-compose"
                  ? { ricercaIniziale: richiesta.termine }
                  : {})}
                titolo={`${dettaglio.stack.name} · docker-compose.yml`}
                // Il salvataggio resta a portata anche a schermo intero: uscire
                // per confermare il lavoro vanificherebbe l'espansione.
                azioni={
                  modificabile ? (
                    <Button
                      variant="primary"
                      loading={salvataggio.running}
                      disabled={!modificato}
                      onClick={() => void salvataggio.run()}
                    >
                      {t.t(modificato ? "dettaglio.salvaRevisione" : "dettaglio.nessunaModifica")}
                    </Button>
                  ) : undefined
                }
              />
            </div>
          }
          destra={
            <div className="space-y-1.5 pl-1">
              {!segretiVisibili && (
                <div className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/8 px-3 py-1.5">
                  <span className="text-xs text-amber-700 dark:text-amber-400">
                    {t.t("dettaglio.sensibiliNascosti")}
                  </span>
                  {/* Rivelare i segreti richiede `stack:write` sul server: a chi
                      può solo consultare lo stack il comando non va offerto. */}
                  {modificabile && (
                    <Button variant="ghost" className="text-xs" onClick={onRivela}>
                      {t.t("dettaglio.mostraPerModificare")}
                    </Button>
                  )}
                </div>
              )}

              <EnvEditor
                value={env}
                onChange={setEnv}
                readOnly={!modificabile || !segretiVisibili}
                richieste={richieste}
                consegnaTutto={consegnaTutto}
                gestite={gestiteDaPortman}
                conDefault={conValorePredefinito}
                {...(richiesta?.tipo === "imposta-variabile" ||
                richiesta?.tipo === "filtra-variabile"
                  ? { daImpostare: richiesta.variabile }
                  : {})}
                altezza={segretiVisibili ? altezzaEditor : altezzaEditor - 38}
              />
            </div>
          }
        />
      </div>

    </div>
  );
}

function SchedaVerifica({
  stackId,
  strategia,
  bozza,
  modificabile,
  segretiVisibili,
  onRivela,
  onAzione,
}: {
  stackId: number;
  strategia: DeployStrategy;
  bozza: Bozza;
  modificabile: boolean;
  segretiVisibili: boolean;
  onRivela: () => void;
  onAzione: (azione: Azione) => void;
}) {
  const { data, error, loading, reload } = useQuery<StackValidation>(`/stacks/${stackId}/validation`);
  const t = useT();

  // Salvare crea la revisione, ricontrollare la mette alla prova: separati
  // lascerebbero a schermo un verdetto che non riguarda più ciò che è salvato.
  const salvaERicontrolla = useAction(async () => {
    await bozza.salvataggio.run();
    reload();
  });

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-sm font-semibold">{t.t("rilascioUi.controlli.titolo")}</h2>
        <p className="mt-0.5 text-sm tenue">{t.t("rilascioUi.controlli.intro")}</p>
      </div>
      <ErrorNotice error={error} />
      <ErrorNotice error={bozza.salvataggio.error} />
      {loading && <Spinner className="tenue" />}

      {data && (
        <PannelloValidazione
          report={data}
          strategia={strategia}
          onAzione={onAzione}
          onRicontrolla={reload}
          env={bozza.env}
          onImposta={modificabile ? bozza.impostaVariabile : undefined}
          variabiliCambiate={bozza.variabiliCambiate}
          segretiVisibili={segretiVisibili}
          onRivela={onRivela}
          salvataggio={{
            esegui: () => void salvaERicontrolla.run(),
            inCorso: salvaERicontrolla.running,
          }}
        />
      )}
    </section>
  );
}

/**
 * Chiede se il ripristino debba anche avviare lo stack.
 *
 * Il solo ripristino salva la revisione e basta: i container restano quelli di
 * prima finché qualcuno non riavvia, e va detto prima di scegliere.
 */
function SceltaRipristino({
  numero,
  puòAvviare,
  puòScrivere,
  inCorso,
  errore,
  onAvvia,
  onSoloRipristino,
  onChiudi,
}: {
  numero: number;
  puòAvviare: boolean;
  puòScrivere: boolean;
  inCorso: boolean;
  errore: unknown;
  onAvvia: () => void;
  onSoloRipristino: () => void;
  onChiudi: () => void;
}) {
  const finestra = useRef<HTMLDialogElement>(null);
  const t = useT();

  // `showModal` dà gratis sfondo, focus intrappolato ed Esc per chiudere.
  useEffect(() => {
    if (!finestra.current?.open) finestra.current?.showModal();
  }, []);

  return (
    <dialog
      ref={finestra}
      onClose={onChiudi}
      className="superficie m-auto w-[calc(100%-2rem)] max-w-md rounded-xl border p-5 text-[var(--testo)] backdrop:bg-black/40"
    >
      <h2 className="text-base font-semibold">{t.t("storico.ripristinareTitolo", { numero })}</h2>
      <p className="mt-2 text-sm tenue">{t.t("storico.ripristinareTesto")}</p>

      <ErrorNotice error={errore} />

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={() => finestra.current?.close()}>
          {t.t("comune.annulla")}
        </Button>
        {puòScrivere && (
          <Button loading={inCorso} onClick={onSoloRipristino}>
            {t.t("storico.soloRipristino")}
          </Button>
        )}
        {puòAvviare && (
          <Button variant="primary" disabled={inCorso} onClick={onAvvia}>
            {t.t("storico.ripristinaAvvia")}
          </Button>
        )}
      </div>
    </dialog>
  );
}

function SchedaStorico({
  stackId,
  esecuzione,
  puòAvviare,
  puòScrivere,
  onRipristinato,
}: {
  stackId: number;
  esecuzione: ReturnType<typeof useEsecuzione>;
  puòAvviare: boolean;
  puòScrivere: boolean;
  onRipristinato: () => void;
}) {
  const revisioni = useQuery<{ revisions: RevisionSummary[] }>(`/stacks/${stackId}/revisions`);
  const t = useT();
  const deploy = useQuery<{ deployments: Deployment[] }>(`/stacks/${stackId}/deployments`);
  // Il numero mostrato di una revisione, dal suo id.
  const numeroRevisione = useMemo(
    () => new Map(revisioni.data?.revisions.map((r) => [r.id, r.revision]) ?? []),
    [revisioni.data],
  );
  const [aperta, setAperta] = useState<number | null>(null);
  const [daRipristinare, setDaRipristinare] = useState<number | null>(null);

  const soloRipristino = useAction(async (numero: number) => {
    await api.post(`/stacks/${stackId}/revisions/${numero}/ripristino`);
    setDaRipristinare(null);
    revisioni.reload();
    onRipristinato();
  });

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {daRipristinare !== null && (
        <SceltaRipristino
          numero={daRipristinare}
          puòAvviare={puòAvviare}
          puòScrivere={puòScrivere}
          inCorso={soloRipristino.running}
          errore={soloRipristino.error}
          onAvvia={() => {
            setDaRipristinare(null);
            void esecuzione.avvia(`/stacks/${stackId}/rollback/${daRipristinare}`);
          }}
          onSoloRipristino={() => void soloRipristino.run(daRipristinare)}
          onChiudi={() => setDaRipristinare(null)}
        />
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">{t.t("storico.revisioni")}</h2>

        {revisioni.data?.revisions.length === 0 && <Empty title={t.t("storico.nessunaRevisione")} />}

        <Card className="divide-y overflow-hidden">
          {revisioni.data?.revisions.map((revisione, i, tutte) => (
            <div key={revisione.id}>
              <div
                className="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm hover:bg-black/5 dark:hover:bg-white/5"
                onClick={() => setAperta(aperta === revisione.revision ? null : revisione.revision)}
              >
                <span className="text-xs tenue">{aperta === revisione.revision ? "▾" : "▸"}</span>
                <span className="font-mono font-medium">#{revisione.revision}</span>
                <span className="min-w-0 flex-1 truncate tenue">
                  {revisione.message ?? t.t("storico.senzaDescrizione")}
                </span>
                <span className="hidden text-xs tenue sm:inline">{formatoData(revisione.createdAt, t)}</span>

                {(puòAvviare || puòScrivere) && (
                  <Button
                    className="text-xs"
                    disabled={esecuzione.esito === "in-corso"}
                    onClick={(evento) => {
                      evento.stopPropagation();
                      setDaRipristinare(revisione.revision);
                    }}
                  >
                    {t.t("storico.ripristina")}
                  </Button>
                )}
              </div>

              {aperta === revisione.revision && (
                <AnteprimaRevisione
                  stackId={stackId}
                  numero={revisione.revision}
                  precedente={tutte[i + 1]?.revision ?? null}
                />
              )}
            </div>
          ))}
        </Card>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">{t.t("storico.operazioni")}</h2>

        {deploy.data?.deployments.length === 0 && <Empty title={t.t("storico.nessunaOperazione")} />}

        <Card className="divide-y overflow-hidden">
          {deploy.data?.deployments.map((operazione) => (
            <RigaOperazione key={operazione.id} operazione={operazione} revisione={operazione.revisionId === null ? undefined : numeroRevisione.get(operazione.revisionId)} />
          ))}
        </Card>
      </section>
    </div>
  );
}

const ESITO_OPERAZIONE = {
  succeeded: { tono: "success", testo: "storico.esitoRiuscita" },
  failed: { tono: "danger", testo: "storico.esitoFallita" },
  cancelled: { tono: "neutral", testo: "storico.esitoAnnullata" },
  running: { tono: "accent", testo: "storico.esitoInCorso" },
} as const;

/** Una operazione dello storico; i log si leggono solo quando la riga si apre. */
function RigaOperazione({ operazione, revisione }: { operazione: Deployment; revisione: number | undefined }) {
  const t = useT();
  const esito = ESITO_OPERAZIONE[operazione.status];

  return (
    <Apribile
      titolo={
        <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span title={t.tempoFa(dataSqlite(operazione.startedAt))}>{formatoData(operazione.startedAt, t)}</span>
          <span className="tenue">{operazione.actor}</span>
          <span>{t.t(NOMI_OPERAZIONE[operazione.action])}</span>
          {revisione !== undefined && <span className="font-mono tenue">{t.t("storico.rev", { n: revisione })}</span>}
          <Badge tone={esito.tono}>{t.t(esito.testo)}</Badge>
          {operazione.durationMs !== null && <span className="tenue">{formattaDurata(operazione.durationMs)}</span>}
        </span>
      }
    >
      {() => <LogOperazione operazione={operazione} />}
    </Apribile>
  );
}

function LogOperazione({ operazione }: { operazione: Deployment }) {
  const t = useT();
  const log = useQuery<{ logs: DeploymentLog[] }>(`/deployments/${operazione.id}/logs`);

  return (
    <div className="space-y-3">
      {operazione.error !== null && (
        <pre className="whitespace-pre-wrap break-words text-xs text-red-700 dark:text-red-400">{operazione.error}</pre>
      )}
      <ErrorNotice error={log.error} />
      {log.loading && <Spinner className="tenue" />}
      {log.data?.logs.length === 0 && <p className="text-sm tenue">{t.t("storico.nessunLog")}</p>}
      {log.data && log.data.logs.length > 0 && (
        <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-zinc-900 p-3 text-xs text-zinc-200">
          {/* Righe separate da "\n", non da <div>: un <div> vuoto non ha altezza e le righe vuote sparirebbero. */}
          {log.data.logs.map((riga) => (
            <Fragment key={riga.sequence}>
              <TestoAnsi segmenti={segmentaAnsi(riga.message)} />
              {"\n"}
            </Fragment>
          ))}
        </pre>
      )}
    </div>
  );
}

/** Creazione di un nuovo stack. */
export function NewStackPage() {
  const t = useT();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [compose, setCompose] = useState(() => t.t("nuovo.composeEsempio"));
  const [env, setEnv] = useState<Record<string, string>>({ TAG: "1.27-alpine" });

  const richieste = useMemo(() => variabiliUsate(compose), [compose]);
  const gestiteDaPortman = useMemo(() => variabiliDiPorta(compose), [compose]);
  const conValorePredefinito = useMemo(() => variabiliConDefault(compose), [compose]);
  const consegnaTutto = useMemo(() => dichiaraStackEnv(compose), [compose]);

  // Gli editor crescono fino al fondo della finestra: `riservato` lascia posto
  // alla barra di salvataggio, che deve restare visibile senza scorrere.
  const areaEditor = useRef<HTMLDivElement>(null);
  const altezzaEditor = useAltezzaDisponibile(areaEditor, { riservato: 84, minima: 320 });

  const creazione = useAction(async () => {
    const { stack } = await api.post<{ stack: Stack }>("/stacks", {
      name,
      description: description || null,
      composeYaml: compose,
      env,
      message: t.t("nuovo.messaggio"),
    });

    navigate(`/stacks/${stack.id}`);
  });

  return (
    <div className="space-y-5">
      <div>
        <a {...linkProps("/stacks")} className="text-sm tenue hover:underline">
          ← {t.t("nav.stack")}
        </a>
        <h1 className="mt-1 text-xl font-semibold">{t.t("stacks.nuovo")}</h1>
        <p className="text-sm tenue">{t.t("nuovo.intro")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t.t("nuovo.nome")} hint={t.t("nuovo.nomeAiuto")}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.t("nuovo.nomeSegnaposto")} autoFocus />
        </Field>

        <Field label={t.t("nuovo.descrizione")}>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>

      <div ref={areaEditor}>
        <PannelloDiviso
          memoria="stack-nuovo"
          sinistra={
            <div className="group space-y-1.5 pr-1">
              <span className="text-sm font-medium">docker-compose.yml</span>
              <CodeEditor
                value={compose}
                onChange={setCompose}
                linguaggio="yaml"
                altezza={altezzaEditor}
                espandibile
              ricercabile
                titolo={t.t("nuovo.titoloEditor")}
              />
            </div>
          }
          destra={
            <div className="space-y-1.5 pl-1">
              <EnvEditor
                value={env}
                onChange={setEnv}
                richieste={richieste}
                consegnaTutto={consegnaTutto}
                gestite={gestiteDaPortman}
                conDefault={conValorePredefinito}
                altezza={altezzaEditor}
              />
            </div>
          }
        />
      </div>

      <ErrorNotice error={creazione.error} />

      <Button variant="primary" loading={creazione.running} disabled={!name} onClick={() => void creazione.run()}>
        {t.t("nuovo.crea")}
      </Button>
    </div>
  );
}

/**
 * Cosa contiene una revisione: per default cosa cambia rispetto alla
 * precedente, a richiesta il contenuto completo. I segreti arrivano mascherati
 * dalla rotta, come in ogni lettura senza `reveal`.
 */
function AnteprimaRevisione({
  stackId,
  numero,
  precedente,
}: {
  stackId: number;
  numero: number;
  precedente: number | null;
}) {
  const questa = useQuery<DettaglioRevisione>(`/stacks/${stackId}/revisions/${numero}`);
  const prima = useQuery<DettaglioRevisione>(
    precedente === null ? null : `/stacks/${stackId}/revisions/${precedente}`,
  );
  // La prima revisione non ha nulla con cui confrontarsi.
  const [vista, setVista] = useState<"differenze" | "completo">(precedente === null ? "completo" : "differenze");
  const t = useT();

  if (questa.error || prima.error) return <div className="px-4 pb-4"><ErrorNotice error={questa.error ?? prima.error} /></div>;
  if (!questa.data || (precedente !== null && !prima.data)) return <div className="px-4 pb-4"><Spinner /></div>;

  const corrente = questa.data;
  const env = Object.entries(corrente.env);

  return (
    <div className="space-y-3 px-4 pb-4 text-xs">
      {precedente !== null && (
        <div className="flex gap-1">
          {(["differenze", "completo"] as const).map((nome) => (
            <Button
              key={nome}
              variant={vista === nome ? "primary" : "ghost"}
              className="text-xs"
              onClick={() => setVista(nome)}
            >
              {nome === "differenze" ? t.t("storico.cosaCambia", { numero: precedente }) : t.t("storico.completo")}
            </Button>
          ))}
        </div>
      )}

      {vista === "differenze" && prima.data ? (
        <DifferenzeRevisione prima={prima.data} dopo={corrente} />
      ) : (
        <>
          <h3 className="font-semibold">{t.t("storico.compose")}</h3>
          <pre className="max-h-96 overflow-auto rounded bg-black/5 p-3 font-mono dark:bg-white/5">{corrente.composeYaml}</pre>
          <h3 className="font-semibold">{t.t("storico.environment")}</h3>
          {env.length === 0 ? (
            <p className="tenue">{t.t("storico.nessunaVariabile")}</p>
          ) : (
            <pre className="max-h-96 overflow-auto rounded bg-black/5 p-3 font-mono dark:bg-white/5">
              {env.map(([nome, valore]) => `${nome}=${valore}`).join("\n")}
            </pre>
          )}
        </>
      )}
    </div>
  );
}

const STILE_RIGA = {
  aggiunta: "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400",
  tolta: "bg-red-500/12 text-red-700 dark:text-red-400",
  uguale: "",
} as const;

const SEGNO = { aggiunta: "+", tolta: "-", uguale: " " } as const;

function DifferenzeRevisione({ prima, dopo }: { prima: DettaglioRevisione; dopo: DettaglioRevisione }) {
  const blocchi = comprimi(differenzaRighe(prima.composeYaml, dopo.composeYaml));
  const variabili = differenzaVariabili(prima.env, dopo.env);
  const composeUguale = blocchi.every((b) => b.tipo === "invariate");
  const t = useT();

  return (
    <>
      <h3 className="font-semibold">{t.t("storico.compose")}</h3>
      {composeUguale ? (
        <p className="tenue">{t.t("storico.invariato")}</p>
      ) : (
        <div className="max-h-96 overflow-auto rounded bg-black/5 py-2 font-mono dark:bg-white/5">
          {blocchi.map((blocco, i) =>
            blocco.tipo === "invariate" ? (
              <div key={i} className="px-3 py-0.5 tenue italic">
                … {t.t("storico.righeInvariate", { n: blocco.conta })}
              </div>
            ) : (
              <div key={i} className={cx("whitespace-pre-wrap break-all px-3", STILE_RIGA[blocco.tipo])}>
                {SEGNO[blocco.tipo]} {blocco.testo}
              </div>
            ),
          )}
        </div>
      )}

      <h3 className="font-semibold">{t.t("storico.environment")}</h3>
      {variabili.length === 0 ? (
        <p className="tenue">{t.t("storico.invariato")}</p>
      ) : (
        <div className="rounded bg-black/5 py-2 font-mono dark:bg-white/5">
          {variabili.map((v) => (
            <div key={v.nome} className="px-3">
              {v.tipo === "aggiunta" && <span className={STILE_RIGA.aggiunta}>+ {v.nome}={v.dopo}</span>}
              {v.tipo === "tolta" && <span className={STILE_RIGA.tolta}>- {v.nome}={v.prima}</span>}
              {v.tipo === "cambiata" && (
                <>
                  {v.nome}: <span className={STILE_RIGA.tolta}>{v.prima}</span> →{" "}
                  <span className={STILE_RIGA.aggiunta}>{v.dopo}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
